import { AsyncPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TimePastPipe } from '@commons/time-past';
import { LoyaltyIcons } from '@core/utils/icons-utils';
import {
  NotificationConfig,
  NotificationIcons,
  NotificationPalette,
  NotificationType,
} from '@models/notification/notification';
import { BROWSERS, DeviceDetectorService } from 'ngx-device-detector';
import { hostBinding } from 'ngxtension/host-binding';
import { Observable, combineLatest, filter, interval, map, of, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-notification-toast',
  standalone: true,
  templateUrl: './notification-toast.component.html',
  styleUrls: ['./notification-toast.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatIconModule, MatButtonModule, MatProgressBarModule, TimePastPipe, AsyncPipe],
})
export class NotificationToastComponent {
  readonly #deviceDetectorService = inject(DeviceDetectorService);

  readonly notificationSig = signal<NotificationConfig | null>(null);

  @Input()
  set notificationConfig(config: NotificationConfig | undefined) {
    if (config === undefined) {
      return;
    }
    this.notificationSig.set(config);
  }

  @Output() dismissNotification = new EventEmitter<number>();

  readonly computedStatusColorSig = hostBinding(
    'style.--status-color',
    computed(() => {
      const notification = this.notificationSig();
      if (notification === null) return NotificationPalette[NotificationType.primary];
      return NotificationPalette[notification.type];
    })
  );

  readonly textSig = computed(() => {
    const notification = this.notificationSig();
    const text = typeof notification?.text === 'string' ? notification.text : '';
    return notification ? text : '';
  });

  readonly valuesSig = toSignal(
    combineLatest([
      toObservable(this.notificationSig).pipe(
        filter((notification): notification is NotificationConfig => !!notification)
      ),
      toObservable(this.notificationSig).pipe(
        filter((notification): notification is NotificationConfig => !!notification),
        switchMap((notification) => this.#setTimerValue(notification.duration))
      ),
    ]).pipe(
      tap(([notification, value]) => {
        if (notification.duration !== undefined && value > 100) {
          this.close(notification.id);
        }
      }),
      map(([_, value]) => value)
    )
  );

  readonly closeIcon = LoyaltyIcons.Close;
  readonly NotificationIcons = NotificationIcons;

  readonly close = (notificationId: number): void => {
    this.dismissNotification.emit(notificationId);
  };

  readonly #setTimerValue = (duration = 0): Observable<number> => {
    if (duration === 0) {
      return of(0);
    }
    const intervalDuration = this.#deviceDetectorService.browser === BROWSERS.FIREFOX ? 10 : 200;
    return interval(intervalDuration).pipe(
      map((timerIteration) => (timerIteration * intervalDuration * 100) / duration)
    );
  };
}
